import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment-timezone";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import HTMLContent from "../components/common/HtmlContent";
import { motion, AnimatePresence } from "framer-motion";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import {
  auth,
  cars,
  coupons,
  rentals,
  contact,
  drivers,
  transferPricing,
  partners,
  tours,
  tourBookings,
  employeeService,
  admins,
} from "../services/api";
import CreateRentalModal from "../components/RentalModal/CreateRentalModal";
import EditRentalModal from "../components/RentalModal/EditRentalModal";
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner";
import NotificationSystem from "../components/Notification/NotificationSystem";
import InsuranceExpirationAlert from "../components/Notifications/InsuranceExpirationAlert";
import MaintenanceNotification from "../components/Notifications/MaintenanceNotification";
import RentalCalendar from "../components/Calendar/RentalCalendar";
import UpcomingPickups from "../components/Calendar/UpcomingPickups";
import VehicleUtilization from "../components/Calendar/VehicleUtilization";
import UpcomingReturns from "../components/Calendar/UpcomingReturns";
import EmployeeManagement from "../components/EmployeeManagement/EmployeeManagement";
import { tabs } from "../components/AdminDashBoard/data/tabs";
import { seasons } from "../components/AdminDashBoard/data/seasons";
import { TbGps } from "react-icons/tb";
import withReactContent from "sweetalert2-react-content";
import CreateTourForm from "../components/TourForm/CreateTourForm";
import EditTourForm from "../components/TourForm/EditTourForm";
import CarDetailsModal from "../components/CarDetailsModal/CarDetailsModal";
import FinanceManagement from "../components/FinanceManagement/FinanceManagement";
import PartnerManagement from "../components/PartnerManagement/PartnerManagement";
import CreateCarModal from "../components/CarDetailsModal/CreateCarModal";
import { generateDailyPlanPDF } from "../utils/pdfGenerator";
import TransferModal from "../components/TransferModal/TransferModal";
import { categories } from "../data/carData";
import TransferePage from "../components/TransferPricing/TransferPricingManagement";
import VehicleInstancesPanel from "../components/VehicleManagement/VehicleInstancesPanel";
import {
  ITEMS_PER_PAGE,
  RENTALS_PER_PAGE,
} from "../components/AdminDashBoard/data/perpage";
import AdminDashBoardTabsContainer from "../components/AdminDashBoard/AdminDashBoardTabsContainer";
import TabsButtons from "../components/AdminDashBoard/Tabs/TabsButtons";

import { handleRentalUpdateSubmit } from "../handlers/rental/rentalHandler";
import { handleEditTour } from "../handlers/tour/editTourHandler";
import { handleCreateTour } from "../handlers/tour/createTourHandler";
import { handleCreateTransfer } from "../handlers/transfer/createTransferHandler";
import { handleSeasonSelect } from "../handlers/season/seasonSelectHandler";
import { handleCarDelete } from "../handlers/car/deleteCarHandler";
import { handleRentalDelete } from "../handlers/rental/rentalDeleteHandler";
import { handleCreateCar } from "../handlers/car/createCarHandler";
import { handleDurationDiscountUpdate } from "../handlers/discount/durationDiscountUpdateHandler";
import { handlePaymentStatusUpdateNew } from "../handlers/payments/paymentStatusUpdateNewHandler";
import { handleAssignDriverToTransfer } from "../handlers/drivers/driverAssignToTranfer";
import { handleCarUpdate } from "../handlers/car/updateCarHandler";
import { handleDriverEdit } from "../handlers/drivers/driverEditHandler";
import { handleDriverCreate } from "../handlers/drivers/driverCreateHandler";
import { handleDriverDelete } from "../handlers/drivers/driverDeleteHandler";
import { handleUnassignDriver } from "../handlers/drivers/unassignDriverHandler";
import { handleTransferDelete } from "../handlers/transfer/deleteTransferHandler";
import { handleTransferStatusUpdate } from "../handlers/transfer/updateTransferStatusHandler";
import { handleCouponEdit } from "../handlers/coupon/couponEditHandler";
import { handleCouponDelete } from "../handlers/coupon/couponDeleteHandler";
import { handleCouponCreate } from "../handlers/coupon/couponCreateHandler";
import { handleRentalStatusUpdate } from "../handlers/rental/rentalStatusUpdateHandler";
import { handleDriverPasswordChange } from "../handlers/drivers/driverPasswordChangeHandler";
import { getDailyTurnover } from "../handlers/turnover/getDaylyTurnoverHandler";
import { getTomorrowTurnover } from "../handlers/turnover/getTomorrowTurnoverHandler";
import { fetchDurationDiscounts } from "../handlers/discount/fetchDurationDiscounts";
import { filterRentals } from "../handlers/rental/filterRentalsHandler";
import { fetchPartners } from "../handlers/fetchPartners";
import { verifyAuth } from "../handlers/auth/verifyAuth";
import { fetchData as executeFetchDataHandler } from "../handlers/fetchDataHandler";
import { getImagePath } from "../handlers/getImagePathHandler";
import { filterCars } from "../handlers/car/filterCarsHandler";
import { paginateCars } from "../handlers/pagination/carsPagination";
import { paginateRentals } from "../handlers/pagination/rentalsPagination";
import { useServerSideRentals } from "./useServerSideRentals";
import { useAllRentals } from "./useAllRentals";

export const useAdminDashboard = () => {
  // Refs
  const formRef = useRef();
  const editTourRef = useRef(null);

  const MySwal = withReactContent(Swal);

  // UI state hooks
  // Ensure all state variables from errors are declared correctly
  const [isLoading, setIsLoading] = useState(true);
  const [carsData, setCarsData] = useState([]);
  const [rentalsData, setRentalsData] = useState([]);
  const [couponsData, setCouponsData] = useState([]);
  const [transfersData, setTransfersData] = useState([]);
  const [employeesData, setEmployeesData] = useState([]);
  const [driversData, setDriversData] = useState([]);
  const [toursData, setToursData] = useState([]);
  const [tourBookingsData, setTourBookingsData] = useState([]);
  const [error, setError] = useState(null);
  const [seasonalPricing, setSeasonalPricing] = useState({});
  const [basePrice, setBasePrice] = useState(0);
  const [selectedRental, setSelectedRental] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditRentalModalOpen, setIsEditRentalModalOpen] = useState(false);
  const [isUpdatingRental, setIsUpdatingRental] = useState(false);
  const [isCreateTransferModalOpen, setIsCreateTransferModalOpen] =
    useState(false);
  const [activeTab, setActiveTab] = useState(
    window.location.hostname === "transfer-athens.online" ? "transfers" : "cars"
  );
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [creatingEmployee, setCreatingEmployee] = useState(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [changingEmployeePasswordState, setChangingEmployeePasswordState] =
    useState(null);
  const [deletingEmployeeId, setDeletingEmployeeId] = useState(null);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedTransmission, setSelectedTransmission] = useState("all");
  const [settingSeasonalPrice, setSettingSeasonalPrice] = useState(null); // For 'setSettingSeasonalPrice' and 'settingSeasonalPrice' errors
  const [editingSeasonalPrice, setEditingSeasonalPrice] = useState(null);
  const [onDurationDiscountUpdate, setOnDurationDiscountUpdate] = useState(false);
  const [updatingCarId, setUpdatingCarId] = useState(null); // For 'setUpdatingCarId' and 'updatingCarId' errors
  const [deletingCarId, setDeletingCarId] = useState(null);
  const [editingCar, setEditingCar] = useState(null); // For 'setEditingCar' and 'editingCar' errors
  const [selectedCar, setSelectedCar] = useState(null);
  const [carDetailsModalOpen, setCarDetailsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreatingCar, setIsCreatingCar] = useState(false);
  const [durationDiscounts, setDurationDiscounts] = useState({
    shortTerm: { discount: 0 },
    mediumTerm: { discount: 5 },
    longTerm: { discount: 10 },
  });
  const [deletingRentalId, setDeletingRentalId] = useState(null);
  const [createCarModalOpen, setCreateCarModalOpen] = useState(false);
  const [updatingPaymentId, setUpdatingPaymentId] = useState(null);
  const [rentalSearch, setRentalSearch] = useState("");
  const [rentalStatusFilter, setRentalStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState({
    startDate: null,
    endDate: null,
  });
  const [creatingCoupon, setCreatingCoupon] = useState(false); // For 'setCreatingCoupon' and 'creatingCoupon' errors
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [deletingCouponId, setDeletingCouponId] = useState(null); // For 'setDeletingCouponId' and 'deletingCouponId' errors
  const [turnoverPage, setTurnoverPage] = useState({ pickups: 1, returns: 1 });
  // Transfer states
  const [transferSearch, setTransferSearch] = useState("");
  const [transferStatusFilter, setTransferStatusFilter] = useState("all");
  const [expandedTransfer, setExpandedTransfer] = useState(null);
  const [deletingTransferId, setDeletingTransferId] = useState(null);
  const [updatingTransferStatus, setUpdatingTransferStatus] = useState(null);
  const [assigningDriverToTransfer, setAssigningDriverToTransfer] =
    useState(null);
  const [transferDateFilter, setTransferDateFilter] = useState({
    startDate: null,
    endDate: null,
  });
  const [transferTimeFilter, setTransferTimeFilter] = useState("all");
  const [transferPartnerFilter, setTransferPartnerFilter] = useState("all");
  const [creatingPartnerTransfer, setCreatingPartnerTransfer] = useState(false);
  // Driver states
  const [driverSearch, setDriverSearch] = useState("");
  const [creatingDriver, setCreatingDriver] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [deletingDriverId, setDeletingDriverId] = useState(null);
  const [changingDriverPassword, setChangingDriverPassword] = useState(null);
  const [driverVehicleTypeFilter, setDriverVehicleTypeFilter] = useState("all");  // Employee pagination
  const [employeeCurrentPage, setEmployeeCurrentPage] = useState(1);
  const [totalRentalPages, setTotalRentalPages] = useState(1);
  // Car/Rental pagination
  const [carPage, setCarPage] = useState(1);
  const [CARS_PER_PAGE, setCarsPerPage] = useState(9);
  const [paginatedCars, setPaginatedCars] = useState([]);
  const [updatingRentalId, setUpdatingRentalId] = useState(null); // For 'setUpdatingRentalId' and 'updatingRentalId' errors  const [paginatedRentals, setPaginatedRentals] = useState([]);
  const [filteredRentals, setFilteredRentals] = useState([]);
  const [isTomorrow, setIsTomorrow] = useState(false);
  const [isRentalModalOpen, setIsRentalModalOpen] = useState(false);
  const [partnersData, setPartners] = useState([]);

  // Server-side rentals hook
  const serverSideRentals = useServerSideRentals();
  const allRentalsData = useAllRentals(serverSideRentals.totalCount);

  // Define a memoized function for refreshing all dashboard data
  // This function will be returned as 'fetchData' for UI components to use
  const refreshDashboardData = React.useCallback(() => {
    executeFetchDataHandler(
      cars, // service object
      rentals, // service object
      coupons, // service object
      contact, // service object
      drivers, // service object
      tours, // service object
      tourBookings, // service object
      employeeService, // service object
      setCarsData,
      setRentalsData,
      setCouponsData,
      setTransfersData,
      setDriversData,
      setToursData,
      setTourBookingsData,
      setEmployeesData,
      setSeasonalPricing,
      setIsLoading,
      setError
    );
  }, [
    cars, rentals, coupons, contact, drivers, tours, tourBookings, employeeService,
    setCarsData, setRentalsData, setCouponsData, setTransfersData, setDriversData,
    setToursData, setTourBookingsData, setEmployeesData, setSeasonalPricing,
    setIsLoading, setError // State setters are stable, services are assumed stable from imports
  ]);

  // Effect for initial data load - if not already handled elsewhere
  useEffect(() => {
    refreshDashboardData();
  }, [refreshDashboardData]); // Runs once on mount and if refreshDashboardData changes (due to its own deps)

  // handle create tour
  const onCreateTour = () =>
    handleCreateTour({ MySwal, formRef, tours, fetchData: executeFetchDataHandler, CreateTourForm, setError, setIsLoading });

  // handle edit tour
  const onEditTour = (tourId) =>
    handleEditTour(tourId, {
      toursData,
      MySwal,
      formRef,
      EditTourForm,
      tours,
      fetchData: executeFetchDataHandler, setError, setIsLoading
    });  // handle rental update submit
  const onRentalUpdate = (updatedData) =>
    handleRentalUpdateSubmit(updatedData, {
      selectedRental,
      setIsUpdatingRental,
      setIsEditRentalModalOpen,
      setSelectedRental,
      fetchData: refreshDashboardData,
      MySwal,
      rentals,
      setError,
      setIsLoading
    });

  // handle season select
  const onSeasonSelect = (carId, basePrice, seasonKey) =>
    handleSeasonSelect(carId, basePrice, seasonKey, {
      seasons,
      setSettingSeasonalPrice, // Uses setSettingSeasonalPrice
      fetchData: executeFetchDataHandler,
      cars,
      setError, setIsLoading
    });
  // handle car create
  const onCarCreate = (formData) =>
    handleCreateCar(formData, {
      setIsCreatingCar,
      cars,
      fetchData: refreshDashboardData,
      setCreateCarModalOpen,
      setError,
      setIsLoading
    });// handle car delete
  const onCarDelete = (carId) =>
    handleCarDelete(carId, { 
      cars, 
      setDeletingCarId, 
      fetchData: refreshDashboardData,
      setError, 
      setIsLoading 
    });// handle rental delete
  const onRentalDelete = (rentalId) =>
    handleRentalDelete(rentalId, { 
      setDeletingRentalId, 
      rentals, 
      fetchData: refreshDashboardData,
      setError, 
      setIsLoading 
    });    // handle payment status update
  const onPaymentStatusUpdate = (rentalId, paymentStatus) =>
    handlePaymentStatusUpdateNew(rentalId, paymentStatus, {
      setUpdatingPaymentId,
      rentals,
      fetchData: refreshDashboardData,
      setError,
      setIsLoading
    });

  // handle car update
  const onHandleCarUpdate = (carId, data) =>
    handleCarUpdate(carId, data, {
      fetchData: executeFetchDataHandler,
      setUpdatingCarId,
      setEditingCar,
      cars,
      rentals,
      coupons,
      contact,
      drivers,
      tours,
      tourBookings,
      employeeService,
      setCarsData,
      setRentalsData,
      setCouponsData,
      setTransfersData,
      setDriversData,
      setToursData,
      setTourBookingsData,
      setEmployeesData,
      setSeasonalPricing,
      setIsLoading,
      setError,
    });
  // handle driver edit
  const onHandleDriverEdit = (driverId) =>
    handleDriverEdit(driverId, {
      setEditingDriver,
      driversData,
      fetchData: refreshDashboardData,
      setIsLoading,
      setError,
    });

  // handle create transfer
  const onCreateTransfer = () => {
    handleCreateTransfer({ transferPricing, setBasePrice, contact, fetchData: executeFetchDataHandler, setError, setIsLoading });
  };

  // handle assign driver to transfer
  const onAssignDriverToTransfer = (transferId) =>
    handleAssignDriverToTransfer(transferId, {
      setAssigningDriverToTransfer,
      transfersData,
      driversData,
      drivers,
      fetchData: executeFetchDataHandler,
      cars,
      rentals,
      coupons,
      contact,
      tours,
      tourBookings,
      employeeService,
      setCarsData,
      setRentalsData,
      setCouponsData,
      setTransfersData,
      setDriversData,
      setToursData,
      setTourBookingsData,
      setEmployeesData,
      setSeasonalPricing,
      setIsLoading,
      setError,
    });

  // handle driver create
  const onCreateDriver = () =>
    handleDriverCreate({
      setCreatingDriver,
      drivers,
      fetchData: executeFetchDataHandler,
      cars,
      rentals,
      coupons,
      contact,
      tours,
      tourBookings,
      employeeService,
      setCarsData,
      setRentalsData,
      setCouponsData,
      setTransfersData,
      setDriversData,
      setToursData,
      setTourBookingsData,
      setEmployeesData,
      setSeasonalPricing,
      setIsLoading,
      setError,
    });

  // handle driver delete
  const onDeleteDriver = (driverId) =>
    handleDriverDelete(driverId, {
      fetchData: executeFetchDataHandler,
      transfersData,
      setDeletingDriverId,
      cars,
      rentals,
      coupons,
      contact,
      drivers,
      tours,
      tourBookings,
      employeeService,
      setCarsData,
      setRentalsData,
      setCouponsData,
      setTransfersData,
      setDriversData,
      setToursData,
      setTourBookingsData,
      setEmployeesData,
      setSeasonalPricing,
      setIsLoading,
      setError,
    });

  // handle unassigned driver
  const onUnassignDriver = (transferId) =>
    handleUnassignDriver(transferId, {
      fetchData: executeFetchDataHandler,
      cars,
      rentals,
      coupons,
      contact,
      drivers,
      tours,
      tourBookings,
      employeeService,
      setCarsData,
      setRentalsData,
      setCouponsData,
      setTransfersData,
      setDriversData,
      setToursData,
      setTourBookingsData,
      setEmployeesData,
      setSeasonalPricing,
      setIsLoading,
      setError,
    });

  // handle transfer delete
  const onDeleteTransfer = (transferId) =>
    handleTransferDelete(transferId, {
      fetchData: executeFetchDataHandler,
      setDeletingTransferId,
      cars,
      rentals,
      coupons,
      contact,
      drivers,
      tours,
      tourBookings,
      employeeService,
      setCarsData,
      setRentalsData,
      setCouponsData,
      setTransfersData,
      setDriversData,
      setToursData,
      setTourBookingsData,
      setEmployeesData,
      setSeasonalPricing,
      setIsLoading,
      setError,
    });

  // Transfer management functions
  // handle transfer status update - Transfer management functions
  const onUpdateTransferStatus = (transferId, status) =>
    handleTransferStatusUpdate(transferId, status, {
      fetchData: executeFetchDataHandler,
      setUpdatingTransferStatus,
      cars,
      rentals,
      coupons,
      contact,
      drivers,
      tours,
      tourBookings,
      employeeService,
      setCarsData,
      setRentalsData,
      setCouponsData,
      setTransfersData,
      setDriversData,
      setToursData,
      setTourBookingsData,
      setEmployeesData,
      setSeasonalPricing,
      setIsLoading,
      setError,
    });
  // handle coupon edit
  const onCouponEdit = (couponData) =>
    handleCouponEdit(couponData, {
      fetchData: refreshDashboardData,
      coupons,
      editingCoupon,
      setEditingCoupon,
      setIsLoading,
      setError,
    });
  // handle coupon delete
  const onCouponDelete = (couponId) =>
    handleCouponDelete(couponId, {
      fetchData: refreshDashboardData,
      setDeletingCouponId,
      setIsLoading,
      setError,
    });
  // handle cooupon create
  const onCouponCreate = (couponData) =>
    handleCouponCreate(couponData, {
      fetchData: refreshDashboardData,
      setCreatingCoupon,
      setIsLoading,
      setError,
    });  // handle rental status update
  const onRentalStatusUpdate = (rentalId, status) =>
    handleRentalStatusUpdate(rentalId, status, {
      fetchData: refreshDashboardData,
      setUpdatingRentalId,
      rentals,
      setIsLoading,
      setError,
    });

  // handle driver password change
  const onDriverPasswordChange = (driverId) =>
    handleDriverPasswordChange(driverId, {
      drivers,
      setChangingDriverPassword, setError
    });

  // get dayly turnover
  const onGetDailyTurnover = () =>
    getDailyTurnover({ ITEMS_PER_PAGE, rentalsData: allRentalsData?.allRentals || [], turnoverPage });

  // get tomorrow turnover
  const onGetTomorrowTurnover = () => getTomorrowTurnover({ ITEMS_PER_PAGE, rentalsData: allRentalsData?.allRentals || [], turnoverPage });

  // filter rentals
  const onFilterRentals = () =>
    filterRentals({
      rentalsData,
      rentalSearch,
      rentalStatusFilter,
      dateFilter, setError
    });

  // Date formatting
  const formatDateTime = (dateTime) => moment(dateTime).format("DD/MM HH:mm");

  return {
    useEffect,
    Swal,
    useNavigate,
    axios,
    DatePicker,
    HTMLContent,
    motion,
    AnimatePresence,
    Header,
    Footer,
    CreateRentalModal,
    EditRentalModal,
    LoadingSpinner,
    NotificationSystem,
    InsuranceExpirationAlert,
    MaintenanceNotification,
    RentalCalendar,
    UpcomingPickups,
    VehicleUtilization,
    UpcomingReturns,
    EmployeeManagement,
    tabs,
    CarDetailsModal,
    FinanceManagement,
    PartnerManagement,
    CreateCarModal,
    generateDailyPlanPDF,
    TransferModal,
    categories,
    TransferePage,
    VehicleInstancesPanel,
    AdminDashBoardTabsContainer,
    TabsButtons,
    fetchPartners,
    verifyAuth,
    getImagePath,
    filterCars,
    paginateCars,
    paginateRentals,
    // Refs
    formRef,
    editTourRef,
    // Loading and errors
    isLoading,
    setIsLoading,
    error,
    setError,
    // Active tab
    activeTab,
    setActiveTab,
    // Cars
    carsData,
    setCarsData,
    editingCar,
    setEditingCar,
    selectedCar,
    setSelectedCar,
    carDetailsModalOpen,
    setCarDetailsModalOpen,
    deletingCarId,
    setDeletingCarId,
    searchQuery,
    setSearchQuery,
    selectedCategory,
    setSelectedCategory,
    selectedTransmission,
    setSelectedTransmission,
    isCreatingCar,
    setIsCreatingCar,
    createCarModalOpen,
    setCreateCarModalOpen,
    CARS_PER_PAGE,
    setCarsPerPage,
    carPage,
    setCarPage,
    paginatedCars,
    setPaginatedCars,    // Rentals (server-side)
    serverSideRentals,
    // Legacy rentals support (for backward compatibility)
    rentalsData: serverSideRentals.rentalsData,
    setRentalsData,
    selectedRental,
    setSelectedRental,
    isEditRentalModalOpen,
    setIsEditRentalModalOpen,
    isUpdatingRental,
    setIsUpdatingRental,
    updatingRentalId,
    setUpdatingRentalId,
    deletingRentalId,
    setDeletingRentalId,
    isRentalModalOpen,
    setIsRentalModalOpen,
    // Server-side rental data
    serverSideRentals,
    // All rentals data for tabs that need complete dataset
    allRentalsData,
    // Server-side rental filters
    rentalSearch: serverSideRentals.search,
    setRentalSearch: serverSideRentals.setSearch,
    rentalStatusFilter: serverSideRentals.statusFilter,
    setRentalStatusFilter: serverSideRentals.setStatusFilter,
    partnerFilter: serverSideRentals.partnerFilter,
    setPartnerFilter: serverSideRentals.setPartnerFilter,
    dateFilter: serverSideRentals.dateFilter,
    setDateFilter: serverSideRentals.setDateFilter,
    startDate: serverSideRentals.startDate,
    setStartDate: serverSideRentals.setStartDate,
    endDate: serverSideRentals.endDate,
    setEndDate: serverSideRentals.setEndDate,
    paymentStatusFilter: serverSideRentals.paymentStatusFilter,
    setPaymentStatusFilter: serverSideRentals.setPaymentStatusFilter,
    // Server-side pagination
    filteredRentals: serverSideRentals.rentalsData,
    setFilteredRentals: () => {}, // No longer needed with server-side
    paginatedRentals: serverSideRentals.rentalsData,
    setPaginatedRentals: () => {}, // No longer needed with server-side
    totalRentalPages: serverSideRentals.totalPages,
    setTotalRentalPages: () => {}, // No longer needed with server-side
    currentPage: serverSideRentals.currentPage,
    setCurrentPage: serverSideRentals.setCurrentPage,
    // Coupons
    couponsData,
    setCouponsData,
    creatingCoupon,
    setCreatingCoupon,
    deletingCouponId,
    setDeletingCouponId,
    editingCoupon,
    setEditingCoupon,
    // Transfers
    transfersData,
    setTransfersData,
    isCreateTransferModalOpen,
    setIsCreateTransferModalOpen,
    transferSearch,
    setTransferSearch,
    transferStatusFilter,
    setTransferStatusFilter,
    expandedTransfer,
    setExpandedTransfer,
    deletingTransferId,
    setDeletingTransferId,
    updatingTransferStatus,
    setUpdatingTransferStatus,
    assigningDriverToTransfer,
    setAssigningDriverToTransfer,
    transferDateFilter,
    setTransferDateFilter,
    transferTimeFilter,
    setTransferTimeFilter,
    transferPartnerFilter,
    setTransferPartnerFilter,
    creatingPartnerTransfer,
    setCreatingPartnerTransfer,
    // Tours
    toursData,
    setToursData,
    tourBookingsData,
    setTourBookingsData,
    // Season & Pricing
    settingSeasonalPrice,
    setSettingSeasonalPrice,
    seasonalPricing,
    setSeasonalPricing,
    editingSeasonalPrice,
    setEditingSeasonalPrice,
    basePrice,
    setBasePrice,
    durationDiscounts,
    setDurationDiscounts,
    // Drivers
    driversData,
    setDriversData,
    creatingDriver,
    setCreatingDriver,
    editingDriver,
    setEditingDriver,
    deletingDriverId,
    setDeletingDriverId,
    driverSearch,
    setDriverSearch,
    driverVehicleTypeFilter,
    setDriverVehicleTypeFilter,
    // Employees
    employeesData,
    setEmployeesData,
    employeeSearch,
    setEmployeeSearch,
    creatingEmployee,
    setCreatingEmployee,
    editingEmployeeId,
    setEditingEmployeeId,
    changingEmployeePasswordState,
    setChangingEmployeePasswordState,    deletingEmployeeId,
    setDeletingEmployeeId,
    employeeCurrentPage,
    setEmployeeCurrentPage,
    // Partners
    partnersData,
    setPartners,
    // Other UI
    isEditModalOpen,
    setIsEditModalOpen,
    expandedCustomer,
    setExpandedCustomer,
    // Payments
    updatingPaymentId,
    setUpdatingPaymentId,
    // Turnover
    turnoverPage,
    setTurnoverPage,
    // SweetAlert2
    MySwal,

    isTomorrow,
    setIsTomorrow,
    onCreateTour,
    onEditTour,
    onRentalUpdate,
    onSeasonSelect,
    onCarCreate,
    onCarDelete,
    onRentalDelete,
    onDurationDiscountUpdate,
    onPaymentStatusUpdate,
    onHandleCarUpdate,
    onHandleDriverEdit,
    onCreateTransfer,
    onAssignDriverToTransfer,
    onCreateDriver,
    onDeleteDriver,
    onUnassignDriver,
    onDeleteTransfer,
    onUpdateTransferStatus,
    onCouponEdit,
    onCouponDelete,
    onCouponCreate,
    onRentalStatusUpdate,
    onDriverPasswordChange,
    onGetDailyTurnover,
    onGetTomorrowTurnover,
    onFilterRentals,
    formatDateTime,
    RENTALS_PER_PAGE,
    partners,
    fetchData: refreshDashboardData, 
    auth,
    cars,
    rentals,
    coupons,
    contact,
    drivers,
    tours,
    tourBookings,
    employeeService,
    fetchDurationDiscounts,
    handleRentalStatusUpdate,
    updatingCarId,         // Error L876
    setUpdatingCarId,      // Added for completeness if needed externally    changingDriverPassword,
    getDailyTurnover: onGetDailyTurnover,
    getTomorrowTurnover: onGetTomorrowTurnover,
    setChangingDriverPassword,
        admins,

  };
};